import React, { useState, useEffect } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { communicationBaseRoute } from "../../../../utils/urls";
import QueuedEmailTab from "./queued_coms/QueuedEmailTab";
import QueuedSMSTab from "./queued_coms/QueuedSMSTab";

function CommunicationStatusTab({ facilityId }) {
  const [emailStatus, setEmailStatus] = useState("send");
  const [smsStatus, setSmsStatus] = useState("send");
  const [loading, setLoading] = useState(false);
  const [hasExistingStatus, setHasExistingStatus] = useState(false);
  const [communicationData, setCommunicationData] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Fetch existing communication status
  const fetchCommunicationStatus = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/status`,
        "GET",
      );

      if (response.success) {
        const data = response.data;
        setEmailStatus(data.emailStatus);
        setSmsStatus(data.smsStatus);
        setCommunicationData(data);
        setHasExistingStatus(true);
      } else {
        // No existing status found, use defaults
        setEmailStatus("send");
        setSmsStatus("send");
        setHasExistingStatus(false);
      }
    } catch (error) {
      // If 404 or communication status not found, use defaults
      setEmailStatus("send");
      setSmsStatus("send");
      setHasExistingStatus(false);
      if (
        error.message !== "Communication status not found for this facility"
      ) {
        toastify(
          "Error fetching communication status: " + error.message,
          "error",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Create new communication status
  const createCommunicationStatus = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/create`,
        "POST",
        {
          emailStatus,
          smsStatus,
        },
      );

      // Handle both success flag and 201 responses (which might not have success flag)
      if (
        response.success ||
        response.status === 201 ||
        (response.data && response._id)
      ) {
        toastify("Communication status created successfully", "success");
        setHasExistingStatus(true);
        setCommunicationData(response.data || response); // Use response directly if data is not present
        fetchCommunicationStatus(); // Refresh data
      } else {
        throw new Error(
          response.error || "Failed to create communication status",
        );
      }
    } catch (error) {
      toastify(
        "Error creating communication status: " + error.message,
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // Update existing communication status
  const updateCommunicationStatus = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/update`,
        "PUT",
        {
          emailStatus,
          smsStatus,
        },
      );

      if (response.success) {
        toastify("Communication status updated successfully", "success");
        setCommunicationData(response.data);
      } else {
        throw new Error(
          response.error || "Failed to update communication status",
        );
      }
    } catch (error) {
      toastify(
        "Error updating communication status: " + error.message,
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = () => {
    if (hasExistingStatus) {
      updateCommunicationStatus();
    } else {
      createCommunicationStatus();
      // 201 status will be handled in the createCommunicationStatus function
    }
  };

  useEffect(() => {
    if (facilityId) {
      fetchCommunicationStatus();
    }
  }, [facilityId]);

  return (
    <div className="communication-status-container">
      <div className="row">
        <div className="col-12">
          <Card>
            <TabView
              activeIndex={activeTabIndex}
              onTabChange={(e) => setActiveTabIndex(e.index)}
            >
              {/* Communication Status Tab */}
              <TabPanel
                header="Communication Status"
                leftIcon="ti ti-settings me-2"
              >
                <div className="communication-status-content">
                  <div className="card-header border-bottom mb-4">
                    <h5 className="mb-1">
                      <i className="ti ti-message me-2"></i>
                      Communication Settings
                    </h5>
                    <small className="text-muted">
                      Control email and SMS communication for this facility
                    </small>
                  </div>

                  {loading ? (
                    <div className="text-center py-4">
                      <i className="ti ti-loader animate-spin me-2"></i>
                      Loading communication settings...
                    </div>
                  ) : (
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group mb-4">
                          <label className="form-label fw-semibold">
                            <i className="ti ti-mail me-2"></i>
                            Email Communication
                          </label>
                          <div className="mt-2">
                            <div className="d-flex align-items-center gap-3">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="emailStatus"
                                  id="emailSend"
                                  checked={emailStatus === "send"}
                                  onChange={() => setEmailStatus("send")}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="emailSend"
                                >
                                  <span className="badge bg-success me-2">
                                    Send
                                  </span>
                                  Allow emails
                                </label>
                              </div>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="emailStatus"
                                  id="emailHold"
                                  checked={emailStatus === "hold"}
                                  onChange={() => setEmailStatus("hold")}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="emailHold"
                                >
                                  <span className="badge bg-warning me-2">
                                    Hold
                                  </span>
                                  Hold emails
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group mb-4">
                          <label className="form-label fw-semibold">
                            <i className="ti ti-message-circle me-2"></i>
                            SMS Communication
                          </label>
                          <div className="mt-2">
                            <div className="d-flex align-items-center gap-3">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="smsStatus"
                                  id="smsSend"
                                  checked={smsStatus === "send"}
                                  onChange={() => setSmsStatus("send")}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="smsSend"
                                >
                                  <span className="badge bg-success me-2">
                                    Send
                                  </span>
                                  Allow SMS
                                </label>
                              </div>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="smsStatus"
                                  id="smsHold"
                                  checked={smsStatus === "hold"}
                                  onChange={() => setSmsStatus("hold")}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="smsHold"
                                >
                                  <span className="badge bg-warning me-2">
                                    Hold
                                  </span>
                                  Hold SMS
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Current Status Display */}
                  {communicationData && !loading && (
                    <div className="row mt-4">
                      <div className="col-12">
                        <div className="alert alert-info">
                          <h6 className="alert-heading">
                            <i className="ti ti-info-circle me-2"></i>
                            Current Status
                          </h6>
                          <div className="row">
                            <div className="col-md-6">
                              <strong>Email:</strong>{" "}
                              <span
                                className={`badge ${
                                  communicationData.emailStatus === "send"
                                    ? "bg-success"
                                    : "bg-warning"
                                }`}
                              >
                                {communicationData.emailStatus}
                              </span>
                            </div>
                            <div className="col-md-6">
                              <strong>SMS:</strong>{" "}
                              <span
                                className={`badge ${
                                  communicationData.smsStatus === "send"
                                    ? "bg-success"
                                    : "bg-warning"
                                }`}
                              >
                                {communicationData.smsStatus}
                              </span>
                            </div>
                          </div>
                          {communicationData.createdAt && (
                            <small className="text-muted d-block mt-2">
                              Last updated:{" "}
                              {new Date(
                                communicationData.createdAt,
                              ).toLocaleString()}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="row mt-4">
                    <div className="col-12">
                      <div className="d-flex justify-content-end gap-2">
                        <Button
                          label="Reset to Default"
                          icon="ti ti-refresh"
                          className="p-button-outlined"
                          onClick={() => {
                            setEmailStatus("send");
                            setSmsStatus("send");
                          }}
                          disabled={loading}
                        />
                        <Button
                          label={
                            hasExistingStatus
                              ? "Update Settings"
                              : "Create Settings"
                          }
                          icon={hasExistingStatus ? "ti ti-edit" : "ti ti-plus"}
                          className="p-button-primary"
                          onClick={handleSubmit}
                          loading={loading}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </TabPanel>

              {/* Queued Emails Tab */}
              <TabPanel header="Queued Emails" leftIcon="ti ti-mail me-2">
                <QueuedEmailTab facilityId={facilityId} />
              </TabPanel>

              {/* Queued SMS Tab */}
              <TabPanel
                header="Queued SMS"
                leftIcon="ti ti-message-circle me-2"
              >
                <QueuedSMSTab facilityId={facilityId} />
              </TabPanel>
            </TabView>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default CommunicationStatusTab;
